import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getFormDefinitions,
  getFormDefinition,
  createFormDefinition,
  publishFormDefinition,
  submitForm,
  getFormSubmissions,
} from '../services/formService';
import { friendlyFormAction, readableSubmissionEntries } from '../utils/formPresentation';
import './FormsPage.css';

export default function FormsPage() {
  const { formId } = useParams();
  const navigate = useNavigate();

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [canManageSelectedForm, setCanManageSelectedForm] = useState(false);
  const [canCreateForms, setCanCreateForms] = useState(false);

  // Form Submission State
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // New Form Definition State
  const [showNewForm, setShowNewForm] = useState(false);
  const [showManageForms, setShowManageForms] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newActionType, setNewActionType] = useState('CREATE_SUPPLY_REQUEST');
  const [creatingForm, setCreatingForm] = useState(false);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFormDefinitions();
      setForms(res.data.forms || []);
      setCanCreateForms(Boolean(res.data.canCreateForms));
    } catch (err) {
      console.error('Failed to load forms:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSelectedForm = useCallback(async (id) => {
    try {
      const res = await getFormDefinition(id);
      setSelectedForm(res.data.form);
      const canManage = Boolean(res.data.canManage);
      setCanManageSelectedForm(canManage);
      setFormData({});
      setSubmitSuccess(false);
      setSubmitError('');

      if (canManage) {
        setSubsLoading(true);
        getFormSubmissions(id)
          .then((sRes) => setSubmissions(sRes.data.submissions || []))
          .catch(() => setSubmissions([]))
          .finally(() => setSubsLoading(false));
      } else {
        setSubmissions([]);
        setSubsLoading(false);
      }
    } catch (err) {
      console.error('Failed to load form details:', err);
    }
  }, []);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  useEffect(() => {
    if (formId) {
      loadSelectedForm(formId);
    } else {
      setSelectedForm(null);
      setCanManageSelectedForm(false);
    }
  }, [formId, loadSelectedForm]);

  const handleCreateDefinition = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreatingForm(true);
    try {
      let defaultFields = [];
      if (newActionType === 'CREATE_SUPPLY_REQUEST') {
        defaultFields = [
          { key: 'itemsNeeded', label: 'Items Needed / Request Summary', type: 'text', required: true },
          { key: 'department', label: 'Department / Program', type: 'text', required: true },
          { key: 'eventName', label: 'Event or Project Name', type: 'text', required: false },
          { key: 'requiredByDate', label: 'Required By Date', type: 'date', required: true },
          { key: 'websiteLink', label: 'Item / Supplier Website Link', type: 'text', required: false },
          { key: 'totalCostIncludingHst', label: 'Estimated Total Cost (inc. HST)', type: 'number', required: false },
          { key: 'quoteNotes', label: 'Quote / Vendor Details', type: 'textarea', required: false },
          { key: 'notes', label: 'Additional Operational Notes', type: 'textarea', required: false },
        ];
      } else {
        defaultFields = [
          { key: 'title', label: 'Task Title', type: 'text', required: true },
          { key: 'description', label: 'Task Description', type: 'textarea', required: false },
          { key: 'dueDate', label: 'Due Date', type: 'date', required: false },
        ];
      }

      const res = await createFormDefinition({
        name: newTitle.trim(),
        title: newTitle.trim(),
        description: newDesc.trim(),
        fields: defaultFields,
        actions: [{ type: newActionType, config: {} }],
      });
      const created = res.data.form;
      setForms((prev) => [created, ...prev]);
      setShowNewForm(false);
      setNewTitle('');
      setNewDesc('');
      navigate(`/forms/${created._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create form');
    } finally {
      setCreatingForm(false);
    }
  };

  const canManageAnyForm = canCreateForms || forms.some((form) => form.canManage) || canManageSelectedForm;

  const handlePublish = async (id) => {
    try {
      const res = await publishFormDefinition(id);
      setSelectedForm(res.data.form);
      setForms((prev) => prev.map((f) => (f._id === id ? res.data.form : f)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to publish form');
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!selectedForm) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitForm(selectedForm._id, { answers: formData });
      setSubmitSuccess(true);
      setFormData({});
      if (canManageSelectedForm) {
        const sRes = await getFormSubmissions(selectedForm._id);
        setSubmissions(sRes.data.submissions || []);
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="forms-page">
      {/* ── Sidebar: Form Templates ── */}
      <aside className="forms-sidebar">
        <div className="forms-sidebar-header">
          <h2>Requests &amp; Forms</h2>
          {canManageAnyForm && <button className="btn btn-ghost btn-sm" onClick={() => setShowManageForms((v) => !v)}>
            Manage Forms
          </button>}
        </div>

        {canManageAnyForm && showManageForms && (
          <div className="forms-manage-panel">
            <button className="btn btn-primary btn-sm" onClick={() => setShowNewForm((v) => !v)}>
              + New Form
            </button>
          </div>
        )}

        {canManageAnyForm && showManageForms && showNewForm && (
          <form className="new-form-box" onSubmit={handleCreateDefinition}>
            <input
              className="form-input"
              placeholder="Form Title *"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              required
            />
            <textarea
              className="form-textarea"
              rows={2}
              placeholder="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
            <label className="form-label-xs">When submitted:</label>
            <select
              className="form-select form-select-sm"
              value={newActionType}
              onChange={(e) => setNewActionType(e.target.value)}
            >
              <option value="CREATE_SUPPLY_REQUEST">Order supplies</option>
              <option value="CREATE_TASK">Create a task</option>
            </select>
            <button type="submit" className="btn btn-primary btn-sm" disabled={creatingForm}>
              {creatingForm ? 'Creating...' : 'Create Form'}
            </button>
          </form>
        )}

        {loading ? (
          <p className="panel-muted">Loading forms...</p>
        ) : forms.length === 0 ? (
          <p className="panel-muted">No forms are available yet.</p>
        ) : (
          <ul className="forms-list">
            {forms.map((f) => (
              <li key={f._id}>
                <Link
                  to={`/forms/${f._id}`}
                  className={`forms-list-item ${selectedForm?._id === f._id ? 'active' : ''}`}
                >
                  <span className="form-icon">📋</span>
                  <div className="form-info">
                    <strong>{f.name || f.title}</strong>
                    <span className="form-meta">{f.status === 'published' ? 'Ready' : 'Draft'}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ── Main Detail / Submission Area ── */}
      <main className="forms-main">
        {!selectedForm ? (
          <div className="forms-empty-state">
            <div>
              <h1>ComSapp Forms &amp; Workflows</h1>
              <p>Choose a request or form to start.</p>
            </div>
          </div>
        ) : (
          <div className="form-active-view">
            <div className="form-active-header">
              <div>
                <h1>{selectedForm.name || selectedForm.title}</h1>
                <p>{selectedForm.description || 'Fill out the details below to submit.'}</p>
                <div className="form-header-pills">
                  <span className={`status-pill ${selectedForm.status === 'published' ? 'active' : ''}`}>
                    {selectedForm.status === 'published' ? 'Ready' : 'Draft'}
                  </span>
                  {canManageSelectedForm && selectedForm.actions?.map((act, idx) => (
                    <span key={idx} className="action-pill">{friendlyFormAction(act.type)}</span>
                  ))}
                </div>
              </div>
              {canManageSelectedForm && selectedForm.status !== 'published' && (
                <button className="btn btn-primary btn-sm" onClick={() => handlePublish(selectedForm._id)}>
                  Publish Form
                </button>
              )}
            </div>

            {submitSuccess && (
              <div className="form-success-banner">
                <span className="check-icon">✓</span>
                <div>
                  <strong>Form submitted successfully!</strong>
                  <p>Your request has been recorded.</p>
                </div>
                <div className="form-success-actions">
                  <Link to="/wrike/supply-requests" className="btn btn-sm btn-primary">View Supply Requests</Link>
                  <button className="btn btn-ghost btn-xs" onClick={() => setSubmitSuccess(false)}>Submit another</button>
                </div>
              </div>
            )}

            {submitError && <div className="files-error">{submitError}</div>}

            {/* Rendered Form Fields */}
            <form onSubmit={handleSubmitForm} className="form-render-card">
              <h3>Submit Request</h3>
              <div className="form-fields-grid">
                {(selectedForm.fields || []).map((field) => {
                  const fieldKey = field.key || field.name;
                  return (
                    <div key={fieldKey} className={`field-group ${field.type === 'textarea' ? 'field-full' : ''}`}>
                      <label className="field-label">
                        {field.label} {field.required && <span className="req">*</span>}
                      </label>

                      {field.type === 'textarea' ? (
                        <textarea
                          className="form-textarea"
                          rows={3}
                          value={formData[fieldKey] || ''}
                          onChange={(e) => setFormData((p) => ({ ...p, [fieldKey]: e.target.value }))}
                          required={field.required}
                        />
                      ) : field.type === 'select' ? (
                        <select
                          className="form-select"
                          value={formData[fieldKey] || ''}
                          onChange={(e) => setFormData((p) => ({ ...p, [fieldKey]: e.target.value }))}
                          required={field.required}
                        >
                          <option value="">Select option...</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt.value || opt} value={opt.value || opt}>
                              {opt.label || opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          className="form-input"
                          value={formData[fieldKey] || ''}
                          onChange={(e) => setFormData((p) => ({ ...p, [fieldKey]: e.target.value }))}
                          required={field.required}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>

            {/* Submissions History */}
            {canManageSelectedForm && <div className="form-submissions-section">
              <h3>Submissions ({submissions.length})</h3>
              {subsLoading ? (
                <p className="panel-muted">Loading submissions...</p>
              ) : submissions.length === 0 ? (
                <p className="panel-muted">No submissions recorded for this form yet.</p>
              ) : (
                <ul className="submissions-list">
                  {submissions.map((sub) => (
                    <li key={sub._id} className="submission-card">
                      <div className="sub-top">
                        <strong>Submitted by: {sub.submittedBy?.name || 'User'}</strong>
                        <span className="sub-date">{new Date(sub.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="sub-data-preview">
                        {readableSubmissionEntries(sub.answers || sub.data, selectedForm.fields || []).map((entry) => (
                          <div key={entry.label} className="submission-field-row">
                            <span>{entry.label}</span>
                            <strong>{entry.value || 'Not answered'}</strong>
                          </div>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>}
          </div>
        )}
      </main>
    </div>
  );
}
